import * as React from 'react';

import {
  useEnterpriseSelfServePrice,
  useEnterpriseSelfServePriceV2,
  useIsDifferentialPricingEnabled,
} from '@coursera-web/wes-common';
import type { ProductPrice, ProductPriceData } from '@coursera-web/wes-common';
import type { LocationDescriptorObject } from 'history';
import { compose, withProps } from 'recompose';

import type { NavigateFunction } from 'js/lib/useRouter';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import useUrlSessionStore from 'js/lib/useUrlSessionStore';
import user from 'js/lib/user';

import type { CheckoutSessionType } from 'bundles/teams-v2/components/TeamsMainApp';
import {
  getQueryFromLocation,
  getValidatedBillingTypeFromLocation,
  getValidatedLearnerCountFromLocation,
} from 'bundles/teams-v2/components/utils';
import { NotificationProvider } from 'bundles/teams-v2/context/NotificationContext';
import { previewTieredPricingEnabled } from 'bundles/teams-v2/utils/experimentUtils';
import { ErrorType, logToSentry } from 'bundles/teams-v2/utils/sentry';
import type {
  PropsFromPromotionEligibility,
  WithPromoCodeToPromotionProps,
} from 'bundles/teams-v2/utils/withEnterprisePromotion';
import {
  withPromoCodeToPromotion,
  withPromotionEligibility,
  withRouterPromoCode,
} from 'bundles/teams-v2/utils/withEnterprisePromotion';

type PropsFromRouter = {
  location: LocationDescriptorObject;
  navigate: NavigateFunction;
  role?: string;
  organizationName?: string;
  organizationType?: string;
  organizationSizeCategory?: string;
};

type MappedProps = {
  learnerCount?: number;
  billingType?: number;
};

export type Props = PropsFromRouter & MappedProps & PropsFromPromotionEligibility & WithPromoCodeToPromotionProps;

export type TeamsContextState = {} & WithPromoCodeToPromotionProps &
  PropsFromPromotionEligibility &
  MappedProps & {
    role?: string;
    organizationName?: string;
    organizationType?: string;
    organizationSizeCategory?: string;
    businessEmail?: string;
    fullName?: string;
    /**
     * @deprecated in favor of priceWithTieredPricing
     */
    defaultProductPrice: ProductPrice | null;
    /**
     * @deprecated in favor of priceWithTieredPricing
     */
    productPrice: ProductPrice | null;
    priceWithTieredPricing: ProductPriceData | null;
    isPriceLoading: boolean;
  };

export const TeamsContext = React.createContext<TeamsContextState>({
  defaultProductPrice: null,
  productPrice: null,
  priceWithTieredPricing: null,
  isPriceLoading: true,
});

export const TeamsContainer = (props: React.PropsWithChildren<Props>) => {
  const {
    learnerCount,
    billingType,
    isPromotionEligible,
    promoCode,
    promotion,
    enterprisePromotion,
    children,
    role,
    organizationName,
    organizationType,
    organizationSizeCategory,
  } = props;

  const enterprisePromotionId = isPromotionEligible ? enterprisePromotion?.id : undefined;
  const isDifferentialPricingEnabled = useIsDifferentialPricingEnabled(true);
  const isTieredPricingEnabled = previewTieredPricingEnabled();

  // Condition to decide which data source to use
  // Use the custom hook for clean data fetching

  const {
    productPrice,
    defaultProductPrice,
    error: legacyPriceFetchError,
  } = useEnterpriseSelfServePrice({
    learnerCount,
    billingType,
    enterprisePromotionId,
    isDifferentialPricingEnabled,
    skipApiCall: isTieredPricingEnabled,
  });

  const {
    data: priceWithTieredPricing,
    error: tieredPriceFetchError,
    loading: isPriceLoading,
  } = useEnterpriseSelfServePriceV2({
    learnerCount,
    billingType,
    enterprisePromotionId,
    isDifferentialPricingEnabled,
    skipApiCall: !isTieredPricingEnabled,
  });

  if (legacyPriceFetchError) {
    logToSentry(legacyPriceFetchError, ErrorType.GraphQLError, {
      message: 'Error fetching product price in TeamsContainer',
      additionalContext: {
        ...legacyPriceFetchError,
      },
    });
    throw legacyPriceFetchError;
  }

  if (tieredPriceFetchError) {
    logToSentry(tieredPriceFetchError, ErrorType.GraphQLError, {
      message: 'Error fetching product price with tiered pricing in TeamsContainer',
    });
    throw tieredPriceFetchError;
  }

  const { sessionStore } = useUrlSessionStore<CheckoutSessionType>();

  const getBusinessEmail = () => {
    // TODO: Use hooks solution for sessionStore state
    if (typeof window === 'undefined') {
      return '';
    }

    let email = sessionStore.getState().businessEmail;
    if (!email) {
      if (user.isAuthenticatedUser()) {
        email = user.get().email_address;
      } else {
        email = '';
      }
    }

    return email;
  };
  const businessEmail = getBusinessEmail();

  const getFullName = () => {
    // TODO: Use hooks solution for sessionStore state
    // useUrlSessionStore causes a 500 on SSR without
    if (typeof window === 'undefined') {
      return '';
    }

    let name = sessionStore.getState().fullName;
    if (!name) {
      if (user.isAuthenticatedUser()) {
        name = user.get().full_name;
      } else {
        name = '';
      }
    }

    return name;
  };

  const fullName = getFullName();

  return (
    <NotificationProvider>
      <TeamsContext.Provider
        value={{
          promotion,
          enterprisePromotion,
          isPromotionEligible,
          promoCode,
          defaultProductPrice,
          productPrice,
          learnerCount,
          billingType,
          role,
          organizationName,
          organizationType,
          organizationSizeCategory,
          businessEmail,
          fullName,
          priceWithTieredPricing,
          isPriceLoading,
        }}
      >
        {children}
      </TeamsContext.Provider>
    </NotificationProvider>
  );
};

const TeamsContainerHOC = compose<Props, PropsFromRouter>(
  // Conditionally find promotion information if not DD
  compose(withRouterPromoCode, withPromoCodeToPromotion, withPromotionEligibility),

  withProps<MappedProps, PropsFromRouter>(({ location }) => {
    const validatedLearnerCount = getValidatedLearnerCountFromLocation(location);

    return {
      learnerCount: validatedLearnerCount ?? undefined,
      billingType: getValidatedBillingTypeFromLocation(location),
    };
  })
)(TeamsContainer);

const TeamsContainerWithRouter: React.LegacyFunctionComponentWithChildren<{}> = (props: {}) => {
  // Discretionary Discounts disable promotions
  const { sessionStore } = useUrlSessionStore<CheckoutSessionType>();
  const location = useLocation();
  const navigate = useNavigate();

  const getDataFromSessionStore = (key: keyof CheckoutSessionType) => {
    // TODO: Use hooks solution for sessionStore state
    if (typeof window === 'undefined') return '';
    return sessionStore.getState()[key];
  };
  // get all query params and session store params
  const role = getQueryFromLocation(location, 'role') || getDataFromSessionStore('role');
  const organizationName =
    getQueryFromLocation(location, 'organizationName') || getDataFromSessionStore('organizationName');
  const organizationType =
    getQueryFromLocation(location, 'organizationType') || getDataFromSessionStore('organizationType');
  const organizationSizeCategory =
    getQueryFromLocation(location, 'organizationSizeCategory') || getDataFromSessionStore('organizationSizeCategory');
  return (
    <TeamsContainerHOC
      {...props}
      location={location}
      navigate={navigate}
      role={role}
      organizationName={organizationName}
      organizationType={organizationType}
      organizationSizeCategory={organizationSizeCategory}
    />
  );
};

export default TeamsContainerWithRouter;
